import {
  BOT_THEME,
  CATEGORY_IDS,
  COMMAND_ACTIONS,
  COMMAND_IDS,
  CONTEXT_TYPES,
  QUICK_ACTION_IDS,
  TEMPLATE_PLACEHOLDERS,
  USER_ROLES,
} from "./constants";

/**
 * Command Categories
 * Each category contains commands organized by functionality
 */
export const commandCategories = [
  // ==========================================
  // CATEGORY 1: QUICK NAVIGATION & ACTIONS
  // ==========================================
  {
    id: CATEGORY_IDS.NAVIGATION,
    title: "Navigation",
    icon: "fa-compass",
    iconBgColor: BOT_THEME.accentBlue,
    order: 1,

    // Global navigation commands (shown when NOT on client page)
    commands: [
      {
        id: COMMAND_IDS.NAV_CLIENTS,
        text: "Navigate to client list",
        prompt: "Show me the client list",
        action: COMMAND_ACTIONS.NAVIGATE,
        route: "/clients",
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER],
      },
      {
        id: COMMAND_IDS.NAV_CLIENTS,
        text: "Navigate to my clients",
        prompt: "Show me my clients",
        action: COMMAND_ACTIONS.NAVIGATE,
        route: "/clients",
        roles: [USER_ROLES.PROVIDER],
      },
      {
        id: COMMAND_IDS.NAV_PROVIDERS,
        text: "Navigate to provider list",
        prompt: "Show me all providers",
        action: COMMAND_ACTIONS.NAVIGATE,
        route: "/providers",
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER],
      },
      {
        id: COMMAND_IDS.NAV_BOOKINGS,
        text: "Open booking requests",
        prompt: "Show me booking requests",
        action: COMMAND_ACTIONS.NAVIGATE,
        route: "/booking-requests",
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: COMMAND_IDS.NAV_REPORTS_BILLING,
        text: "Navigate to billing report",
        prompt: "Take me to billing report",
        action: COMMAND_ACTIONS.NAVIGATE,
        route: "/admin/reports/billing",
        roles: [USER_ROLES.ADMIN],
      },
      {
        id: COMMAND_IDS.NAV_REPORTS_PAYROLL,
        text: "Navigate to payroll report",
        prompt: "Take me to payroll report",
        action: COMMAND_ACTIONS.NAVIGATE,
        route: "/admin/reports/payroll",
        roles: [USER_ROLES.ADMIN],
      },
      {
        id: COMMAND_IDS.NAV_SETTINGS,
        text: "Go to settings",
        prompt: "Open system settings",
        action: COMMAND_ACTIONS.NAVIGATE,
        route: "/admin/settings",
        roles: [USER_ROLES.ADMIN],
      },
      {
        id: COMMAND_IDS.QA_NEW_CLIENT,
        text: "Add a new client",
        prompt: "Add a new client",
        action: COMMAND_ACTIONS.NAVIGATE,
        route: "/clients/create",
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER],
      },
      {
        id: COMMAND_IDS.QA_NEW_PROVIDER,
        text: "Add a new provider",
        prompt: "Add a new provider",
        action: COMMAND_ACTIONS.NAVIGATE,
        route: "/providers/create",
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER],
      },
    ],

    // Client-specific navigation (shown when ON client page with patient mode enabled)
    contextCommands: [
      {
        id: COMMAND_IDS.CLIENT_NAV_PROFILE,
        text: "View profile",
        prompt: `Navigate to ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s profile page`,
        action: COMMAND_ACTIONS.NAVIGATE,
        route: `/clients/${TEMPLATE_PLACEHOLDERS.PATIENT_ID}/profile`,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: COMMAND_IDS.CLIENT_NAV_SERVICES,
        text: "View services",
        prompt: `Navigate to ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s services page`,
        action: COMMAND_ACTIONS.NAVIGATE,
        route: `/clients/${TEMPLATE_PLACEHOLDERS.PATIENT_ID}/services`,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: COMMAND_IDS.CLIENT_NAV_PROVIDERS,
        text: "View providers",
        prompt: `Navigate to ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s providers page`,
        action: COMMAND_ACTIONS.NAVIGATE,
        route: `/clients/${TEMPLATE_PLACEHOLDERS.PATIENT_ID}/providers`,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: COMMAND_IDS.CLIENT_NAV_CONTACTS,
        text: "View contacts",
        prompt: `Navigate to ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s contacts page`,
        action: COMMAND_ACTIONS.NAVIGATE,
        route: `/clients/${TEMPLATE_PLACEHOLDERS.PATIENT_ID}/contacts`,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: COMMAND_IDS.CLIENT_NAV_DOCUMENTS,
        text: "View documents",
        prompt: `Navigate to ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s documents page`,
        action: COMMAND_ACTIONS.NAVIGATE,
        route: `/clients/${TEMPLATE_PLACEHOLDERS.PATIENT_ID}/documents`,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: COMMAND_IDS.CLIENT_NAV_NOTES,
        text: "View clinic notes",
        prompt: `Navigate to ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s clinic notes page`,
        action: COMMAND_ACTIONS.NAVIGATE,
        route: `/clients/${TEMPLATE_PLACEHOLDERS.PATIENT_ID}/notes`,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: COMMAND_IDS.CLIENT_NAV_BUDGET,
        text: "View budget",
        prompt: `Navigate to ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s budget page`,
        action: COMMAND_ACTIONS.NAVIGATE,
        route: `/clients/${TEMPLATE_PLACEHOLDERS.PATIENT_ID}/budget`,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: COMMAND_IDS.CLIENT_NAV_ENGAGEMENT_NOTES,
        text: "View engagement notes",
        prompt: `Navigate to ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s engagement notes page`,
        action: COMMAND_ACTIONS.NAVIGATE,
        route: `/clients/${TEMPLATE_PLACEHOLDERS.PATIENT_ID}/engagement-notes`,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],
  },

  // ==========================================
  // CATEGORY 2: PATIENT INSIGHTS
  // ==========================================
  {
    id: CATEGORY_IDS.PATIENT_INSIGHTS,
    title: "Patient Insights",
    icon: "fa-user-md",
    iconBgColor: BOT_THEME.accentPurple,
    order: 4,

    commands: [],

    // All prompts require patient context
    contextCommands: [
      {
        id: "pi_patient_history",
        text: `Summarize ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s history and key risk factors`,
        prompt: `Summarize ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s complete patient history including key risk factors, diagnosis, and clinical journey`,
        action: COMMAND_ACTIONS.SUMMARIZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "pi_assessments_due",
        text: `What assessments are due for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}?`,
        prompt: `What assessments are due for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}? List any overdue or upcoming assessments.`,
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "pi_symptom_progression",
        text: `Show symptom progression for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}`,
        prompt: `Show symptom progression over time for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME} based on engagement notes and assessments`,
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "pi_medications",
        text: `List medications for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}`,
        prompt: `List medications and any changes made recently for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}`,
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "pi_trends",
        text: `What trends stand out for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}?`,
        prompt: `Are there any trends around ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s journey that stand out? Analyze patterns in symptoms, engagement, and outcomes.`,
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "pi_handover_summary",
        text: `Generate handover summary for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}`,
        prompt: `Generate a concise handover summary for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME} including current status, recent updates, and key considerations`,
        action: COMMAND_ACTIONS.GENERATE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],
  },

  // ==========================================
  // CATEGORY 3: CLINICAL DECISIONS
  // ==========================================
  {
    id: CATEGORY_IDS.CLINICAL_DECISIONS,
    title: "Clinical Decisions",
    icon: "fa-stethoscope",
    iconBgColor: "#4CAF50",
    order: 7,

    commands: [],

    // All prompts require patient context
    contextCommands: [
      {
        id: "cd_effective_interventions",
        text: `What interventions worked for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}?`,
        prompt: `What interventions were most effective for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}? Analyze treatment outcomes and response patterns.`,
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "cd_care_plan_changes",
        text: `Who modified ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s care plan?`,
        prompt: `Which clinician last modified the care plan for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME} and why? Show recent care plan changes and rationale.`,
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "cd_treatment_rationale",
        text: `Show treatment rationale for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}`,
        prompt: `Show rationale behind treatment changes for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}. Explain clinical reasoning and decision-making.`,
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],
  },

  // ==========================================
  // CATEGORY 4: INTERVENTIONS & OUTCOMES
  // ==========================================
  {
    id: CATEGORY_IDS.INTERVENTIONS_OUTCOMES,
    title: "Interventions & Outcomes",
    icon: "fa-chart-bar",
    iconBgColor: "#FF9800",
    order: 5,

    // General commands (caseload-wide)
    commands: [
      {
        id: "io_colleagues_comparison",
        text: "Compare progress against colleagues' patients",
        prompt:
          "Compare progress against colleagues and their patients data. Show comparative outcomes and benchmarks.",
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: false,
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "io_not_meeting_goals",
        text: "Highlight patients not meeting treatment goals",
        prompt:
          "Highlight patients who are not meeting treatment goals. Identify patients requiring intervention or plan adjustments.",
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: false,
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],

    // Patient-specific command
    contextCommands: [
      {
        id: "io_therapy_outcomes",
        text: `Summarize therapy outcomes for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}`,
        prompt: `Summarize outcomes of last 5 therapy sessions for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}. Show progress, engagement, and clinical improvements.`,
        action: COMMAND_ACTIONS.SUMMARIZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],
  },

  // ==========================================
  // CATEGORY 5: CASELOAD & WORKFLOW
  // ==========================================
  {
    id: CATEGORY_IDS.CASELOAD_WORKFLOW,
    title: "Caseload & Workflow",
    icon: "fa-tasks",
    iconBgColor: "#2196F3",
    order: 2,

    // All general commands
    commands: [
      {
        id: "cw_charts_signing",
        text: "What charts need signing today?",
        prompt:
          "What charts need signing today? Show all unsigned charts awaiting provider signature.",
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: false,
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],

    contextCommands: [],
  },

  // ==========================================
  // CATEGORY 6: DATA & ANALYTICS
  // ==========================================
  {
    id: CATEGORY_IDS.DATA_ANALYTICS,
    title: "Data & Analytics",
    icon: "fa-chart-line",
    iconBgColor: "#9C27B0",
    order: 8,

    // General commands (caseload-wide)
    commands: [
      {
        id: "da_caseload_diagnosis",
        text: "Compare caseload progress by diagnosis",
        prompt:
          "Compare caseload progress by diagnosis. Show outcomes and trends grouped by diagnosis type.",
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: false,
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "da_monthly_productivity",
        text: "Pull monthly productivity and outcomes summary",
        prompt:
          "Pull monthly productivity and outcomes summary. Show key metrics, caseload statistics, and performance indicators.",
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: false,
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],

    // Context-aware: adapts based on patient context
    contextCommands: [
      {
        id: "da_assessment_scores",
        text: `Show PHQ-9 and GAD-7 trends for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}`,
        prompt: `Show trendlines for PHQ-9 and GAD-7 scores for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}. Display score progression over time.`,
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],
  },

  // ==========================================
  // CATEGORY 7: COMMUNICATION & COLLABORATION
  // ==========================================
  {
    id: CATEGORY_IDS.COMMUNICATION_COLLABORATION,
    title: "Communication & Collaboration",
    icon: "fa-comments",
    iconBgColor: "#00BCD4",
    order: 6,

    // General commands
    commands: [
      {
        id: "cc_co_managed_patients",
        text: "Which patients are co-managed with OT or SLP?",
        prompt:
          "Which patients are co-managed with OT or SLP? List patients with multidisciplinary care teams.",
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: false,
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],

    contextCommands: [],
  },

  // ==========================================
  // CATEGORY 8: COMPLIANCE & BILLING
  // ==========================================
  {
    id: CATEGORY_IDS.COMPLIANCE_BILLING,
    title: "Compliance & Billing",
    icon: "fa-file-invoice-dollar",
    iconBgColor: "#F44336",
    order: 9,

    // All general commands
    commands: [
      {
        id: "cb_missing_notes",
        text: "Show missing or incomplete session notes",
        prompt:
          "Show missing or incomplete session notes. Identify documentation gaps requiring attention.",
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: false,
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "cb_claims_pending",
        text: "Pull all claims pending verification",
        prompt:
          "Pull all claims pending verification. Show billing claims awaiting review or approval.",
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: false,
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "cb_charts_signature",
        text: "Which charts are awaiting signature?",
        prompt:
          "Which charts are awaiting signature? List all unsigned documentation requiring provider signature.",
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: false,
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],

    contextCommands: [],
  },

  // ==========================================
  // CATEGORY 9: KNOWLEDGE & GUIDANCE
  // ==========================================
  {
    id: CATEGORY_IDS.KNOWLEDGE_GUIDANCE,
    title: "Knowledge & Guidance",
    icon: "fa-graduation-cap",
    iconBgColor: "#607D8B",
    order: 10,

    commands: [],

    // Context-aware: adapts to patient diagnosis if available
    contextCommands: [
      {
        id: "kg_best_practices",
        text: `Best practice guidelines for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s diagnosis`,
        prompt: `Summarize latest best-practice guidelines for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s diagnosis. Provide evidence-based recommendations.`,
        action: COMMAND_ACTIONS.SUMMARIZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
      {
        id: "kg_evidence_support",
        text: `Evidence supporting ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}'s intervention plan`,
        prompt: `Provide key evidence supporting intervention plan for ${TEMPLATE_PLACEHOLDERS.PATIENT_NAME}. Show research and clinical evidence backing current treatment approach.`,
        action: COMMAND_ACTIONS.ANALYZE,
        requiresContext: true,
        contextType: CONTEXT_TYPES.PATIENT,
        replacements: {
          [TEMPLATE_PLACEHOLDERS.PATIENT_NAME]: "name",
          [TEMPLATE_PLACEHOLDERS.PATIENT_ID]: "id",
        },
        roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
      },
    ],
  },
];

/**
 * Quick Actions (Bottom Bar)
 * Fast access buttons for common tasks
 */
export const quickActions = [
  {
    id: QUICK_ACTION_IDS.TODAYS_CALENDAR,
    label: "Today's Calendar",
    icon: "fa-calendar-day",
    action: COMMAND_ACTIONS.NAVIGATE,
    route: "/calendar",
    prompt: "Show me today's calendar",
    roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
  },
  {
    id: QUICK_ACTION_IDS.NAV_CLIENTS,
    label: "Client List",
    icon: "fa-users",
    action: COMMAND_ACTIONS.NAVIGATE,
    route: "/clients",
    prompt: "Show me the client list",
    roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER],
  },
  {
    id: QUICK_ACTION_IDS.NAV_CLIENTS,
    label: "My Clients",
    icon: "fa-user-friends",
    action: COMMAND_ACTIONS.NAVIGATE,
    route: "/clients",
    prompt: "Show me my clients",
    roles: [USER_ROLES.PROVIDER],
  },
  {
    id: QUICK_ACTION_IDS.ALERTS,
    label: "Alerts",
    icon: "fa-bell",
    action: COMMAND_ACTIONS.NAVIGATE,
    route: "/tasks",
    prompt: "Show me my alerts",
    roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER],
  },
  {
    id: QUICK_ACTION_IDS.ALERTS,
    label: "My Tasks",
    icon: "fa-check-square",
    action: COMMAND_ACTIONS.NAVIGATE,
    route: "/tasks",
    prompt: "Show me my tasks",
    roles: [USER_ROLES.PROVIDER],
  },
  {
    id: QUICK_ACTION_IDS.DASHBOARD,
    label: "Dashboard",
    icon: "fa-chart-line",
    action: COMMAND_ACTIONS.NAVIGATE,
    route: "/dashboard",
    prompt: "Take me to the dashboard",
    roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
  },
  {
    id: COMMAND_IDS.QA_NEW_TASK,
    label: "New Task",
    icon: "fa-tasks",
    action: COMMAND_ACTIONS.NAVIGATE,
    route: "/tasks/create",
    prompt: "Create a new task",
    roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
  },
  {
    id: COMMAND_IDS.QA_CREATE_ROOM,
    label: "Create Meeting Room",
    icon: "fa-video",
    action: COMMAND_ACTIONS.NAVIGATE,
    route: "/rooms/create",
    prompt: "Create a meeting room",
    roles: [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.PROVIDER],
  },
];
